// UPPDRAG-19 PR-A — pure helper for the AuthRequiredModal flow.
//
// `buildAuthUrl` produces the URL that the modal's "Skapa konto" /
// "Logga in" buttons navigate to. It encodes the user's destination
// (`returnUrl`) so Auth.tsx can redirect them back after a successful
// auth instead of dropping them on the homepage.
//
// Pulled out into a pure function so vitest can guard against the
// classic encoding bugs (double-encoding, missing slash, accidental
// query-param duplication when callers chain it through navigate()).

export type AuthMode = "login" | "signup";

const DEFAULT_RETURN_URL = "/";
const AUTH_BASE_PATH = "/auth";

/**
 * Build the URL to navigate to when the user clicks "Skapa konto" /
 * "Logga in" in the AuthRequiredModal.
 *
 * Format: `/auth?mode={signup|login}&returnUrl={encoded path}`
 *
 * - `returnUrl` is URI-component-encoded so query strings + slashes
 *   round-trip correctly through Auth.tsx's `searchParams.get`.
 * - Empty/null/undefined returnUrl falls back to `/` (homepage) — Auth.tsx
 *   will redirect there post-login, matching the pre-PR-A behaviour.
 */
export function buildAuthUrl(mode: AuthMode, returnUrl?: string | null): string {
  const params = new URLSearchParams();
  params.set("mode", mode);
  const target = returnUrl && returnUrl.length > 0 ? returnUrl : DEFAULT_RETURN_URL;
  params.set("returnUrl", target);
  return `${AUTH_BASE_PATH}?${params.toString()}`;
}

/**
 * Read + sanitize a `returnUrl` query param from Auth.tsx's
 * `searchParams.get("returnUrl")`. Defends against open-redirect:
 *
 *   - Returns "/" if the param is missing/empty.
 *   - Returns "/" if the param is an absolute URL (starts with http://,
 *     https://, or //) — never let a query param send us to an external
 *     site post-login.
 *   - Otherwise returns the raw path (already starts with "/" by virtue
 *     of the encoding round-trip).
 */
export function safeReturnUrl(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_RETURN_URL;
  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://") ||
    raw.startsWith("//")
  ) {
    return DEFAULT_RETURN_URL;
  }
  // Must start with "/" — otherwise we'd land on a relative path that
  // could be confused for the auth page itself.
  if (!raw.startsWith("/")) return DEFAULT_RETURN_URL;
  return raw;
}
